import { useId, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { FrameworkAgreement, Vehicle } from '@dashboard/contracts';
import { useAllFleetVehicles, useCreateAssignment } from '../../api/queries';
import { ModalCard } from '../ui/modal-dialog';
import { MutationError } from '../platform/form-helpers';
import { LoadingState } from '../ui/states';

/**
 * Declaring a vehicle onto an agreement.
 *
 * The act with legal weight in the whole module: it is what an authority later
 * reports to a regulator under its own name. So the dialog says what it is
 * doing in a sentence rather than presenting two pickers and a Save, and it is
 * gated by `fleet.disclose` rather than by whatever lets somebody fix a plate.
 *
 * The period defaults to the agreement's start, because that is the answer
 * nine times out of ten and typing it again is how it gets typed wrong.
 */
export function DeclareDialog({
  agreement,
  onClose,
}: {
  agreement: FrameworkAgreement;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const titleId = useId();
  const baseId = useId();
  const create = useCreateAssignment();

  /* In-service vehicles only: declaring a lorry that is off the road is a
     mistake the API refuses, and offering it here would be offering a choice
     that fails on save. */
  const vehicles = useAllFleetVehicles();

  const [vehicleId, setVehicleId] = useState('');
  const [startsOn, setStartsOn] = useState(agreement.startsOn ?? agreement.awardedOn ?? '');
  const [endsOn, setEndsOn] = useState('');

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (vehicleId === '' || startsOn === '' || create.isPending) return;
    create.mutate(
      {
        agreementId: agreement.id,
        vehicleId,
        startsOn,
        endsOn: endsOn === '' ? null : endsOn,
      },
      { onSuccess: onClose },
    );
  };

  return (
    <ModalCard
      titleId={titleId}
      title={t('fleet.declare.title')}
      onClose={onClose}
      panelClassName="modal__panel--narrow-form"
    >
      <form className="fleet-form" onSubmit={submit}>
        <p className="cell-secondary">{t('fleet.declare.intro', { agreement: agreement.title })}</p>

        {vehicles.isPending ? (
          <LoadingState rows={2} />
        ) : (
          <label className="editor-field" htmlFor={`${baseId}-vehicle`}>
            <span>{t('fleet.declare.vehicle')}</span>
            <select
              id={`${baseId}-vehicle`}
              className="select"
              value={vehicleId}
              required
              autoFocus
              onChange={(event) => setVehicleId(event.target.value)}
            >
              <option value="">{t('fleet.agreementForm.choose')}</option>
              {vehicles.items.map((vehicle: Vehicle) => (
                <option key={vehicle.id} value={vehicle.id}>
                  {vehicle.registrationPlate} — {vehicle.category}
                  {vehicle.party ? ` — ${vehicle.party.name}` : ''}
                </option>
              ))}
            </select>
          </label>
        )}

        <div className="fleet-form__row">
          <label className="editor-field" htmlFor={`${baseId}-from`}>
            <span>{t('fleet.declare.startsOn')}</span>
            <input
              id={`${baseId}-from`}
              className="input"
              type="date"
              value={startsOn}
              required
              onChange={(event) => setStartsOn(event.target.value)}
            />
          </label>

          <label className="editor-field" htmlFor={`${baseId}-to`}>
            <span>{t('fleet.declare.endsOn')}</span>
            <input
              id={`${baseId}-to`}
              className="input"
              type="date"
              value={endsOn}
              onChange={(event) => setEndsOn(event.target.value)}
            />
            {/* Empty means "still serving", which is a different statement from
                a date far in the future and is what most declarations are. */}
            <span className="cell-secondary">{t('fleet.declare.endsOnHint')}</span>
          </label>
        </div>

        <MutationError error={create.error} />

        <div className="form-actions">
          <button type="button" className="button button--ghost" onClick={onClose}>
            {t('editor.cancel')}
          </button>
          <button
            type="submit"
            className="button button--primary"
            disabled={vehicleId === '' || startsOn === '' || create.isPending}
          >
            {create.isPending ? t('editor.saving') : t('fleet.declare.submit')}
          </button>
        </div>
      </form>
    </ModalCard>
  );
}
